import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CancelIcon from "@mui/icons-material/Cancel";
import { tokens } from "../theme/tokens";
import { BOOKING_HISTORY_COLUMNS, statusStyle } from "../shared/bookingHistoryShared";

const columns = BOOKING_HISTORY_COLUMNS;

/**
 * Same grid as Transaction history — optional cancel handler and loading state.
 */
export default function BookingHistoryDataTable({
  rows,
  loading = false,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onCancel,
  onDownload,
  downloadingBookingId = null,
  emptyMessage = "No bookings found.",
  densePagination = false,
}) {
  const showCancel = typeof onCancel === "function";
  const showDownload = typeof onDownload === "function";
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [blockedOpen, setBlockedOpen] = React.useState(false);
  const [pendingCancelRow, setPendingCancelRow] = React.useState(null);

  const isPastBooking = (row) => {
    const dateVal = row?.checkOut || row?.checkIn;
    if (!dateVal) return false;
    const bookingDate = new Date(dateVal);
    if (Number.isNaN(bookingDate.getTime())) return false;
    bookingDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookingDate < today;
  };

  const askCancelConfirm = (row) => {
    if (isPastBooking(row)) {
      setBlockedOpen(true);
      return;
    }
    setPendingCancelRow(row);
    setConfirmOpen(true);
  };

  const closeCancelConfirm = () => {
    setConfirmOpen(false);
    setPendingCancelRow(null);
  };

  const handleConfirmCancel = () => {
    if (pendingCancelRow) onCancel?.(pendingCancelRow);
    closeCancelConfirm();
  };

  return (
    <>
      <TableContainer
        sx={{
          width: "100%",
          borderRadius: `${tokens.radiusMd}px`,
          border: `1px solid ${tokens.border}`,
        }}
      >
        <Table size="small" aria-label="Booking history table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  sx={{
                    minWidth: column.minWidth,
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "#fff",
                    bgcolor: tokens.brand,
                    borderBottom: `2px solid ${tokens.accent}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={32} sx={{ color: tokens.accent }} />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {rows.map((row, rowIndex) => {
                  const sr = page * rowsPerPage + rowIndex + 1;
                  return (
                    <TableRow
                      hover
                      key={String(row.bookingId ?? row.orderId ?? rowIndex)}
                      sx={{
                        "&:nth-of-type(even)": { bgcolor: "rgba(10, 30, 64, 0.02)" },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      {columns.map((column) => {
                        if (column.id === "srNo") {
                          return (
                            <TableCell
                              key={column.id}
                              align="center"
                              sx={{ color: tokens.textMuted, fontWeight: 600 }}
                            >
                              {sr}
                            </TableCell>
                          );
                        }
                        if (column.id === "actions") {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <Tooltip title="View details">
                                <span>
                                  <IconButton
                                    size="small"
                                    aria-label="view"
                                    onClick={() => onView?.(row)}
                                    sx={{
                                      color: tokens.accent,
                                      "&:hover": { bgcolor: tokens.accentSoft },
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {showDownload ? (
                                <Tooltip title="Download PDF voucher">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      aria-label="download pdf"
                                      disabled={
                                        row.bookingId == null ||
                                        row.bookingId === "" ||
                                        downloadingBookingId === row.bookingId
                                      }
                                      onClick={() => onDownload(row)}
                                      sx={{ "&:hover": { bgcolor: tokens.successSoft } }}
                                    >
                                      {downloadingBookingId === row.bookingId ? (
                                        <CircularProgress size={18} sx={{ color: tokens.success }} />
                                      ) : (
                                        <DownloadIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                              {showCancel ? (
                                <Tooltip title="Cancel booking">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="cancel"
                                      disabled={(() => {
                                        const u = String(row.status || "").toUpperCase();
                                        const cancelled =
                                          u.includes("CANCEL") || u === "CANCELEED";
                                        const noId =
                                          row.bookingId == null ||
                                          row.bookingId === "";
                                        return cancelled || noId;
                                      })()}
                                      onClick={() => askCancelConfirm(row)}
                                      sx={{
                                        color: tokens.error,
                                        "&:hover": { bgcolor: tokens.errorSoft },
                                      }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </TableCell>
                          );
                        }
                        if (column.id === "status") {
                          const display =
                            row.status === ""
                              ? "—"
                              : String(row.status).replace(/_/g, " ");
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: statusStyle(row.status),
                                  fontWeight: 700,
                                  fontSize: "0.8125rem",
                                }}
                              >
                                {display}
                              </Typography>
                            </TableCell>
                          );
                        }
                        const value = row[column.id];
                        const text =
                          column.format != null
                            ? column.format(value, row)
                            : value ?? "—";
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align || "left"}
                            sx={{
                              fontSize: "0.8125rem",
                              verticalAlign: "top",
                              ...(column.multiline
                                ? {
                                    maxWidth: 280,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    lineHeight: 1.45,
                                  }
                                : {}),
                            }}
                          >
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{
                                fontWeight: column.id === "orderNumber" ? 600 : 400,
                                color: tokens.text,
                              }}
                            >
                              {text}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {!loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 6, color: tokens.textMuted }}
                    >
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {onPageChange && onRowsPerPageChange ? (
        <TablePagination
          rowsPerPageOptions={densePagination ? [5, 10, 25] : [10, 25, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          sx={{
            borderTop: `1px solid ${tokens.border}`,
            mt: 0,
            ".MuiTablePagination-toolbar": { px: 0.5 },
          }}
        />
      ) : null}

      <Dialog open={confirmOpen} onClose={closeCancelConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm cancellation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: tokens.text }}>
            Are you sure you want to cancel this booking?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeCancelConfirm} variant="outlined">
            No
          </Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            Yes, cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={blockedOpen} onClose={() => setBlockedOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancellation not allowed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: tokens.text }}>
            You cannot cancel past bookings.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setBlockedOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
